import { Button, EmptyState, FieldBlock, Input, Select, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { FormRow, FormScreen, FormSection } from '../components/FormScreen.tsx'
import { api } from '../lib/api.ts'

type Conta = {
  id: string
  name: string
  kind: 'personal' | 'company'
  planId: string | null
  planName: string | null
  isOwner: boolean
  companyLegalName: string | null
  companyTradeName: string | null
  companyCnpj: string | null
  companyAddress: string | null
}

/** Mascara visual 00.000.000/0000-00 conforme digita. */
function formatarCnpj(bruto: string) {
  const d = bruto.replace(/\D/g, '').slice(0, 14)
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
}

/**
 * Configuracoes da CONTA (2026-08-26): tipo (PF/empresa), dados da empresa e
 * plano. Aberta pelo menu do usuario; so o DONO edita.
 */
export function AccountSettingsPage() {
  const toast = useToast()
  const qc = useQueryClient()

  const detalhe = useQuery({
    queryKey: ['account'],
    queryFn: async () => (await api.account.$get()).json(),
  })
  const planos = useQuery({
    queryKey: ['plans'],
    queryFn: async () => (await api.plans.$get()).json(),
  })

  const [form, setForm] = useState<Conta | null>(null)
  useEffect(() => {
    if (detalhe.data?.account) setForm(detalhe.data.account as Conta)
  }, [detalhe.data])

  const dono = form?.isOwner ?? false
  const muda = <K extends keyof Conta>(campo: K, valor: Conta[K]) =>
    setForm((f) => (f ? { ...f, [campo]: valor } : f))

  const salvar = useMutation({
    mutationFn: async () => {
      const f = form!
      const resposta = await api.account.$patch({
        json: {
          name: f.name.trim(),
          kind: f.kind,
          companyLegalName: f.kind === 'company' ? f.companyLegalName?.trim() || null : null,
          companyTradeName: f.kind === 'company' ? f.companyTradeName?.trim() || null : null,
          companyCnpj: f.kind === 'company' ? f.companyCnpj?.trim() || null : null,
          companyAddress: f.kind === 'company' ? f.companyAddress?.trim() || null : null,
        },
      })
      const corpo = (await resposta.json()) as { account?: Conta; error?: { message?: string } }
      if (!resposta.ok) throw new Error(corpo.error?.message ?? 'Não foi possível salvar.')
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['account'] })
      toast.push({ tone: 'success', message: 'Conta atualizada.' })
    },
    onError: (erro) =>
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível salvar.' }),
  })

  const trocarPlano = useMutation({
    mutationFn: async (planId: string | null) =>
      (await api.plans.account[':accountId'].$put({ param: { accountId: form!.id }, json: { planId } })).json(),
    onSuccess: (_dados, planId) => {
      setForm((f) => (f ? { ...f, planId } : f))
      void qc.invalidateQueries({ queryKey: ['account'] })
      // o rail e os guards leem o bundle — invalidar reflete a troca na hora
      void qc.invalidateQueries({ queryKey: ['feature-toggle-bundle'] })
      toast.push({ tone: 'success', message: 'Plano da conta atualizado.' })
    },
    onError: (erro) =>
      toast.push({ tone: 'danger', message: erro instanceof Error ? erro.message : 'Não foi possível trocar o plano.' }),
  })

  const persistiveis = (c: Conta) => ({
    name: c.name.trim(),
    kind: c.kind,
    companyLegalName: c.kind === 'company' ? c.companyLegalName?.trim() || null : null,
    companyTradeName: c.kind === 'company' ? c.companyTradeName?.trim() || null : null,
    companyCnpj: c.kind === 'company' ? c.companyCnpj?.trim() || null : null,
    companyAddress: c.kind === 'company' ? c.companyAddress?.trim() || null : null,
  })
  const alterado =
    form !== null &&
    detalhe.data?.account !== undefined &&
    JSON.stringify(persistiveis(form)) !== JSON.stringify(persistiveis(detalhe.data.account as Conta))

  if (detalhe.isError || (detalhe.data && !detalhe.data.account)) {
    return (
      <div className="wsset">
        <EmptyState
          icon="fas fa-id-card"
          title="Não foi possível carregar a conta"
          description="Verifique sua conexão e tente novamente."
          action={
            <Button variant="primary" onClick={() => void detalhe.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      </div>
    )
  }
  if (!form) {
    return (
      <div className="wsset">
        <div className="wsset__card" aria-busy>
          <div className="wsset__skeleton" />
          <div className="wsset__skeleton" style={{ width: '60%' }} />
        </div>
      </div>
    )
  }

  const salvarValido = dono && alterado && form.name.trim().length >= 2 && !salvar.isPending

  return (
    <FormScreen
      title="Minha conta"
      subtitle={form.name}
      dirty={alterado}
      dirtyHint={dono ? 'Tudo salvo' : 'Somente leitura — só o dono da conta edita'}
      saveLabel="Salvar"
      saveDisabled={!salvarValido}
      saving={salvar.isPending}
      onSubmit={() => salvarValido && salvar.mutate()}
      footerExtra={
        <Button variant="ghost" disabled={!alterado} onClick={() => detalhe.data?.account && setForm(detalhe.data.account as Conta)}>
          Descartar
        </Button>
      }
    >
      <FormSection title="Tipo de conta">
        <div className="wsset__kinds" role="radiogroup" aria-label="Tipo de conta">
          <button
            type="button"
            role="radio"
            aria-checked={form.kind === 'personal'}
            className={`wsset__kind${form.kind === 'personal' ? ' wsset__kind--on' : ''}`}
            disabled={!dono}
            onClick={() => muda('kind', 'personal')}
          >
            <i className="fas fa-user" aria-hidden />
            <span>
              <strong>Pessoa física</strong>
              <small>Uso pessoal, sem dados de empresa</small>
            </span>
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={form.kind === 'company'}
            className={`wsset__kind${form.kind === 'company' ? ' wsset__kind--on' : ''}`}
            disabled={!dono}
            onClick={() => muda('kind', 'company')}
          >
            <i className="fas fa-building" aria-hidden />
            <span>
              <strong>Empresa</strong>
              <small>Razão social, CNPJ e endereço</small>
            </span>
          </button>
        </div>
      </FormSection>

      <FormSection title="Identificação">
        <FormRow cols="1fr">
          <FieldBlock label={form.kind === 'company' ? 'Nome da conta' : 'Seu nome'} required>
            {({ id }) => (
              <Input id={id} value={form.name} disabled={!dono} onChange={(e) => muda('name', e.target.value)} />
            )}
          </FieldBlock>
        </FormRow>

        {form.kind === 'company' ? (
          <>
            <FormRow cols="1fr 1fr 1fr">
              <FieldBlock label="CNPJ">
                {({ id }) => (
                  <Input
                    id={id}
                    placeholder="00.000.000/0000-00"
                    value={form.companyCnpj ?? ''}
                    disabled={!dono}
                    onChange={(e) => muda('companyCnpj', formatarCnpj(e.target.value))}
                  />
                )}
              </FieldBlock>
              <FieldBlock label="Razão social">
                {({ id }) => (
                  <Input
                    id={id}
                    value={form.companyLegalName ?? ''}
                    disabled={!dono}
                    onChange={(e) => muda('companyLegalName', e.target.value)}
                  />
                )}
              </FieldBlock>
              <FieldBlock label="Nome fantasia">
                {({ id }) => (
                  <Input
                    id={id}
                    value={form.companyTradeName ?? ''}
                    disabled={!dono}
                    onChange={(e) => muda('companyTradeName', e.target.value)}
                  />
                )}
              </FieldBlock>
            </FormRow>
            <FormRow cols="1fr">
              <FieldBlock label="Endereço">
                {({ id }) => (
                  <Input
                    id={id}
                    value={form.companyAddress ?? ''}
                    disabled={!dono}
                    onChange={(e) => muda('companyAddress', e.target.value)}
                  />
                )}
              </FieldBlock>
            </FormRow>
          </>
        ) : null}
      </FormSection>

      <FormSection title="Plano">
        <FormRow cols="minmax(220px, 420px)">
          <FieldBlock
            label="Plano da conta"
            hint="Vale para todos os workspaces da conta. A troca vale na hora, sem precisar salvar."
          >
            {({ id }) => (
              <Select<string>
                id={id}
                value={form.planId ?? 'SEM_PLANO'}
                disabled={!dono || trocarPlano.isPending}
                options={[
                  { value: 'SEM_PLANO', label: 'Sem plano (todos os módulos)' },
                  ...(planos.data?.plans ?? [])
                    .filter((p) => p.isActive || p.id === form.planId)
                    .map((p) => ({ value: p.id, label: p.name })),
                ]}
                onChange={(valor) => trocarPlano.mutate(valor === 'SEM_PLANO' ? null : valor)}
              />
            )}
          </FieldBlock>
        </FormRow>
      </FormSection>
    </FormScreen>
  )
}
